import { forwardRef, useEffect, useRef, useState } from "react";

import { useHistory } from "react-router-dom";

import { useAppState } from "@/components/AppStateProvider";
import Box from "@/components/Box";
import { CMP_PERMISSION_ADMIN } from "@/components/organisations/constants";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { CMP_CHECKOUT_URL } from "@/core/urls";
import useCmpRelatedUrl from "@/hooks/organisations/useCmpRelatedUrl";
import { useShopPermissions } from "@/hooks/useShopPermissions";
import ChevronComponent from "@/tpo/Chevron";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Group from "@/tpo/Group";
import { Error } from "@/tpo/InputWrapper";
import SegmentedControl from "@/tpo/SegmentedControl";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import styled from "styled-components";

import { PAYEE_ORGANISATION, PAYEE_PATIENT } from "../constants";
import AutoApplyDiscountSuggestions from "./ui/AutoApplyDiscountSuggestions";
import { BasketItem } from "./ui/BasketItem";
import { BasketTotals } from "./ui/BasketTotals";
import { OrganisationPayeeOptions } from "./ui/OrganisationPayeeOptions";
import PatientLocation from "./ui/PatientLocation";
import { PatientPayeeOptions } from "./ui/PatientPayeeOptions";

const LineItemStack = styled(Box)`
  .BasketItemUI {
    border-bottom: 1px solid ${({ theme }) => theme.colors.haze};
    padding-bottom: 20px;
  }
`;

export const OrganisationBasketListing = forwardRef((props, ref) => {
  const { organisation, permissions } = useOrganisationContext();
  const isAdmin = permissions?.includes(CMP_PERMISSION_ADMIN);
  const {
    basket,
    removeProductFromBasket,
    updateBasketDetails,
    refetchBasket,
    setProductQtyInOrganisationBasket,
    setSupplememtQtyInOrganisationBasket,
    applyDiscount,
    removeDiscount,
    discountErrors,
    toggleMultiplePatientsForBasket,
    loading
  } = useOrganisationBasketContext();
  const { canPlaceOrders } = useShopPermissions();
  const history = useHistory();

  const { resolvedUrls } = useCmpRelatedUrl();

  const initialisedDiscountInput = useRef(false);
  const [discountInputValue, setDiscountInputValue] = useState("");

  useEffect(() => {
    if (!initialisedDiscountInput.current) {
      if (basket?.appliedTradeDiscount?.code) {
        initialisedDiscountInput.current = true;
        setDiscountInputValue(basket.appliedTradeDiscount.code);
      }
    } else {
      setDiscountInputValue(basket?.appliedTradeDiscount?.code || "");
    }
  }, [basket?.appliedTradeDiscount]);

  const { selectTestProductId } = useTestProductModalContext();

  const { setPractitionerBasketOpen: setBasketOpen } = useAppState();
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  useEffect(() => {
    if (!organisation) return;
    refetchBasket();
    setOnboardingComplete(organisation?.onboardingStatus === "complete");
  }, [organisation, refetchBasket]);

  const lineItems = [];

  const getItemPrices = item => {
    return {
      price: item.pricePerUnit * item.quantity,
      rrpPrice: item.rrpPerUnit * item.quantity,
      discountValue: item.discountPerUnit * item.quantity
    };
  };

  if (basket) {
    [...basket.productLineItems]
      .sort((a, b) => {
        const sortKeyA = a.productOption
          ? `${a.product.name}-${a.productOption?.name}-${a.clinicLocation?.clinic?.name}`
          : a.product.name;
        const sortKeyB = b.productOption
          ? `${b.product.name}-${b.productOption?.name}-${b.clinicLocation?.clinic?.name}`
          : b.product.name;

        if (sortKeyA < sortKeyB) {
          return -1;
        }
        if (sortKeyA > sortKeyB) {
          return 1;
        }
        return 0;
      })
      .forEach((item, idx) => {
        if (item.clinicLocation) {
          lineItems.push(
            <BasketItem
              {...getItemPrices(item)}
              basketLineItem={item}
              showSelectPatientPerLine={basket?.multiplePatientsEnabled}
              showDutchTestQuestions={
                (basket?.multiplePatientsEnabled && item.hasDutchTest) ||
                (!basket?.multiplePatientsEnabled &&
                  idx === basket.productLineItems.length - 1 &&
                  basket?.hasDutchTest)
              }
              patient={item.patient}
              id={item.id}
              error={item.invalidReason}
              symbol={basket?.currencySymbol}
              onClick={() => {
                setBasketOpen(false);
                selectTestProductId(item.product.id);
              }}
              key={item.id}
              name={item.product.name}
              subname={item.productOption?.testProductOptionFamily?.name}
              clinicName={item.clinicLocation.clinic.name}
              address={item.clinicLocation.address}
              fees={item.clinicFeesPerUnit * item.quantity}
              count={item.quantity}
              removeItemFromBasket={() =>
                removeProductFromBasket(
                  item.product.id,
                  item.productOption?.id,
                  item.clinicLocation?.id
                )
              }
              setQty={
                !basket.purchaseAsStock
                  ? undefined
                  : qty =>
                      setProductQtyInOrganisationBasket({
                        productId: item.product.id,
                        productOptionId: item.productOption?.id,
                        clinicLocationId: item.clinicLocation?.id,
                        quantity: qty
                      })
              }
              addons={item.addons.map(addon => ({
                key: addon.id,
                id: addon.id,
                name: addon.product.name,
                ...getItemPrices(addon),
                product: addon.product,
                parentProduct: item.product,
                invalidReason: addon.invalidReason,
                isRequiredAddon: !!item.product.requiredAddons?.length
              }))}
            />
          );
        } else if (item.productOption) {
          lineItems.push(
            <BasketItem
              {...getItemPrices(item)}
              basketLineItem={item}
              showSelectPatientPerLine={basket?.multiplePatientsEnabled}
              showDutchTestQuestions={
                (basket?.multiplePatientsEnabled && item.hasDutchTest) ||
                (!basket?.multiplePatientsEnabled &&
                  idx === basket.productLineItems.length - 1 &&
                  basket?.hasDutchTest)
              }
              patient={item.patient}
              id={item.id}
              symbol={basket?.currencySymbol}
              error={item.invalidReason}
              onClick={() => {
                setBasketOpen(false);
                selectTestProductId(item.product.id);
              }}
              key={item.id}
              name={item.product.name}
              subname={item.productOption?.testProductOptionFamily?.name}
              count={item.quantity}
              removeItemFromBasket={() =>
                removeProductFromBasket(item.product.id, item.productOption.id)
              }
              setQty={
                !basket.purchaseAsStock
                  ? undefined
                  : qty =>
                      setProductQtyInOrganisationBasket({
                        productId: item.product.id,
                        productOptionId: item.productOption.id,
                        quantity: qty
                      })
              }
              addons={item.addons.map(addon => ({
                key: addon.id,
                id: addon.id,
                name: addon.product.name,
                ...getItemPrices(addon),
                product: addon.product,
                parentProduct: item.product,
                invalidReason: addon.invalidReason,
                isRequiredAddon: !!item.product.requiredAddons?.length
              }))}
            />
          );
        } else {
          lineItems.push(
            <BasketItem
              {...getItemPrices(item)}
              basketLineItem={item}
              showSelectPatientPerLine={basket?.multiplePatientsEnabled}
              showDutchTestQuestions={
                (basket?.multiplePatientsEnabled && item.hasDutchTest) ||
                (!basket?.multiplePatientsEnabled &&
                  idx === basket.productLineItems.length - 1 &&
                  basket?.hasDutchTest)
              }
              id={item.id}
              patient={item.patient}
              symbol={basket?.currencySymbol}
              error={item.invalidReason}
              onClick={() => {
                setBasketOpen(false);
                selectTestProductId(item.product.id);
              }}
              key={item.id}
              name={item.product.name}
              subname={item.productOption?.testProductOptionFamily?.name}
              count={item.quantity}
              removeItemFromBasket={() => removeProductFromBasket(item.product.id)}
              setQty={
                !basket.purchaseAsStock
                  ? undefined
                  : qty =>
                      setProductQtyInOrganisationBasket({
                        productId: item.product.id,
                        quantity: qty
                      })
              }
              addons={item.addons.map(addon => ({
                key: addon.id,
                id: addon.id,
                name: addon.product.name,
                ...getItemPrices(addon),
                product: addon.product,
                parentProduct: item.product,
                invalidReason: addon.invalidReason,
                isRequiredAddon: !!item.product.requiredAddons?.length
              }))}
            />
          );
        }
      });
    [...basket.supplementLineItems]
      .sort((a, b) => {
        const sortKeyA = a.supplement.name;
        const sortKeyB = b.supplement.name;

        if (sortKeyA < sortKeyB) {
          return -1;
        }
        if (sortKeyA > sortKeyB) {
          return 1;
        }
        return 0;
      })
      .forEach(item => {
        lineItems.push(
          <BasketItem
            showSelectPatientPerLine={false}
            error={item.invalidReason}
            key={item.id}
            name={item.supplement.name}
            symbol={basket?.currencySymbol}
            price={item.fullPricePerUnit * item.quantity}
            count={item.quantity}
            setQty={qty => {
              setSupplememtQtyInOrganisationBasket({
                supplementId: item.supplement.id,
                quantity: qty
              });
            }}
          />
        );
      });
  }

  if (lineItems.length === 0) {
    return (
      <Stack gap={[20, 20, 40]}>
        <Box as="h2" fontFamily="gilroyBold" fontSize={[18, 18, 28]}>
          Basket summary
        </Box>
        <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} data-testid="emptyBasketMessage">
          Basket empty - add products to continue
        </Box>
      </Stack>
    );
  }

  return (
    <>
      <Box as="h2" fontFamily="gilroyBold" fontSize={[24, 24, 28]}>
        {basket.purchaseAsStock ? "Stock basket summary" : "Patient basket summary"}
      </Box>
      {!!basket?.shippingAddress?.country && !!basket?.patient && (
        <PatientLocation country={basket.shippingAddress.country} mt={2} />
      )}
      <Spacer py={[0, 0, 2]} />
      <LineItemStack>{lineItems}</LineItemStack>
      <Spacer py={[2, 2, 20]} />
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]}>
          Discount
        </Box>
        <Spacer py={[2, 2, 15]} />
        <Stack>
          <FloatingLabelInput
            editable={!basket.appliedTradeDiscount && !loading}
            label="Enter Discount"
            onChange={e => {
              setDiscountInputValue(e.target.value);
            }}
            rightIcon={
              basket.appliedTradeDiscount ? (
                <ButtonV2
                  type="button"
                  disabled={loading}
                  sx={{
                    color: "red",
                    fontSize: 18,
                    textTransform: "unset",
                    letterSpacing: "unset",
                    fontFamily: "gilroyMedium",
                    p: 0
                  }}
                  onClick={removeDiscount}
                >
                  Remove
                </ButtonV2>
              ) : (
                <ButtonV2
                  type="button"
                  disabled={loading}
                  sx={{
                    color: "green",
                    fontSize: 18,
                    textTransform: "unset",
                    letterSpacing: "unset",
                    fontFamily: "gilroyMedium",
                    p: 0
                  }}
                  onClick={() => {
                    applyDiscount(discountInputValue);
                  }}
                >
                  Apply
                </ButtonV2>
              )
            }
            value={discountInputValue}
          />
          {!basket.appliedTradeDiscount && (
            <AutoApplyDiscountSuggestions
              organisationId={organisation?.id}
              onApply={applyDiscount}
              disabled={loading}
            />
          )}
          {/* discountErrors should only be if a discount was NOT applied i.e. we should have no discount on the basket */}
          {/* discountInvalidReason should only be if a discount code is on the basket but is now not valid e.g. they switched to a stock basket */}
          {discountErrors &&
            !basket.appliedTradeDiscount &&
            discountErrors.map(error => <Error error={error} key={error} />)}
          {!!basket.discountInvalidReason && basket.appliedTradeDiscount && (
            <Error error={basket.discountInvalidReason} />
          )}
        </Stack>
      </Box>
      <Spacer py={[2, 2, 20]} />
      <Stack alignItems="flex-end" gap={0}>
        {onboardingComplete ? (
          <>
            <Group alignItems="center" gap={20} flexWrap="wrap" justifyContent="flex-end">
              <Box fontFamily="gilroyBold" fontSize={16}>
                Who will pay?
              </Box>
              <SegmentedControl
                data-testid="payee-segmented-control"
                data={[
                  {
                    label: "patient",
                    value: PAYEE_PATIENT,
                    unSelectedColor: basket.purchaseAsStock ? "midGrey" : undefined
                  },
                  {
                    label: "business",
                    value: PAYEE_ORGANISATION
                  }
                ]}
                onChange={payee => updateBasketDetails({ payee })}
                value={basket?.payee}
                height={40}
                bg="haze"
                disabled={loading}
              />
            </Group>
            <Spacer py={2} />
          </>
        ) : (
          <Box
            borderRadius={5}
            bg="#E44C4B"
            p={24}
            mb={20}
            width="100%"
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            data-testid="stripe-incomplete-checkout-message"
          >
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} color="#FFFFFF" lineHeight={1.5}>
              {isAdmin ? (
                <>
                  Patient payment options are currently unavailable. To enable patient payments and
                  earn commission, please{" "}
                  <a
                    href={`/cmp/organisations/${organisation?.hashId}/dashboard/business`}
                    onClick={e => {
                      e.preventDefault();
                      setBasketOpen(false);
                      history.push(`/cmp/organisations/${organisation?.hashId}/dashboard/business`);
                    }}
                    style={{ color: "#FFFFFF", textDecoration: "underline" }}
                  >
                    complete your Stripe account setup
                  </a>
                  .
                </>
              ) : (
                "Patient payment options are currently unavailable. Please contact your organisation admin to complete Stripe account setup."
              )}
            </Box>
          </Box>
        )}
        {basket?.payee === PAYEE_PATIENT && (
          <>
            <PatientPayeeOptions
              basket={basket}
              onBasketUpdate={fields => updateBasketDetails({ ...fields, payee: PAYEE_PATIENT })}
              onTogglingMultiplePatientsOption={v => {
                toggleMultiplePatientsForBasket(v);
              }}
              showCommissionSlider={true}
            />
          </>
        )}
        {basket?.payee === PAYEE_ORGANISATION && (
          <OrganisationPayeeOptions
            basket={basket}
            onboardingComplete={onboardingComplete}
            onBasketUpdate={fields => updateBasketDetails({ ...fields, payee: PAYEE_ORGANISATION })}
            onTogglingMultiplePatientsOption={v => {
              toggleMultiplePatientsForBasket(v);
            }}
          />
        )}
        <Spacer py={15} />
        <BasketTotals basket={basket} payee={basket?.payee} showShipping={props.showShipping} />
        {props.showCheckoutButton && (
          <Group alignItems="center" gap={20} flexWrap="wrap" justifyContent="flex-end" mt={6}>
            <ButtonV2
              onClick={() => {
                setBasketOpen(false);
              }}
              color="black"
              rightIcon={<ChevronComponent />}
              size={["sm", "sm", "md"]}
            >
              Continue Shopping
            </ButtonV2>
            <ButtonV2
              onClick={() => {
                history.push(resolvedUrls[CMP_CHECKOUT_URL]);
                setBasketOpen(false);
              }}
              color="green"
              rightIcon={<ChevronComponent />}
              size={["sm", "sm", "md"]}
              disabled={!canPlaceOrders}
            >
              Proceed to Checkout
            </ButtonV2>
          </Group>
        )}
      </Stack>
    </>
  );
});
